window.RS7GTPageScroll = class {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    this.stops = [];
    this.lastWheel = 0;
    this.lockUntil = 0;
    this.resetWheelGesture();
    root.setAttribute('data-page-scroll', '');
    document.documentElement.setAttribute('data-rs7-page-scroll', '');
    this.prepareRefresh();
    this.onTriggerRefresh = () => this.refresh();
    this.onMediaRefresh = () => {
      this.prepareRefresh();
      // Finish the media-query refresh before restoring the selected page.
      // ScrollTrigger can defer it while scrolling, leaving old positions in
      // individual triggers that later responsive image loads would restore.
      window.ScrollTrigger.refresh();
    };
    window.ScrollTrigger?.addEventListener('refresh', this.onTriggerRefresh);
    window.ScrollTrigger?.addEventListener('matchMedia', this.onMediaRefresh);
    const options = { signal: this.controller.signal };
    window.addEventListener('wheel', event => this.onWheel(event), { ...options, passive: false });
    window.addEventListener('touchstart', event => this.onTouchStart(event), { ...options, passive: true });
    window.addEventListener('touchmove', event => this.onTouchMove(event), { ...options, passive: false });
    ['touchend', 'touchcancel'].forEach(name => window.addEventListener(name, () => {
      this.touch = null;
      this.queueSettle();
    }, options));
    window.addEventListener('keydown', event => this.onKey(event), options);
    window.addEventListener('scroll', () => {
      if (this.height === window.innerHeight && this.width === window.innerWidth && !this.restoreKey) {
        this.positionKey = this.stops[this.currentIndex()]?.key;
      }
      this.queueSceneSync();
      this.queueSettle();
    }, { ...options, passive: true });
    window.addEventListener('pointerdown', event => {
      this.scrollbarHeld = event.clientX >= document.documentElement.clientWidth;
    }, options);
    window.addEventListener('pointerup', () => {
      this.scrollbarHeld = false;
      this.queueSettle();
    }, options);
    window.addEventListener('hashchange', () => this.navigateHash(), options);
    document.addEventListener('click', event => this.onLink(event), options);
    root.addEventListener('focusin', event => this.onFocus(event), options);
    root.addEventListener('rs7gt:collage-measure', () => root.refresh(), options);
  }

  currentIndex() {
    if (this.targetKey) {
      const target = this.stops.findIndex(stop => stop.key === this.targetKey);
      if (target >= 0) return target;
    }
    return this.stops.reduce((nearest, stop, index) =>
      Math.abs(stop.y - window.scrollY) < Math.abs(this.stops[nearest].y - window.scrollY) ? index : nearest, 0);
  }

  prepareRefresh(force = false) {
    const resized = this.height !== window.innerHeight || this.width !== window.innerWidth;
    if (!force && !resized) return;
    // A resize can clamp scrollY before the debounced layout refresh runs.
    // Retain the position selected while the previous dimensions were valid.
    this.restoreKey ||= this.targetKey || (resized && this.positionKey) || this.stops[this.currentIndex()]?.key;
    this.cancel();
    this.height = window.innerHeight;
    this.width = window.innerWidth;
    this.root.style.setProperty('--rs7-page-height', `${this.height}px`);
  }

  measureOverlap() {
    // The collage's horizontal travel determines its vertical scene height.
    // Measure it before the static wrappers and suppress recursive refreshes.
    this.root.querySelectorAll('rs7gt-collage[data-launch-collage]').forEach(collage => collage.measure?.(false));
    if (this.root.motionQuery.matches) {
      this.clearOverlap();
      return;
    }
    const previous = this.sceneByWrapper;
    const scenes = [...this.root.querySelectorAll('#MainContent > .shopify-section')]
      .filter(wrapper => wrapper.offsetHeight && wrapper.querySelector('section'))
      .map(wrapper => ({ wrapper, content: wrapper.firstElementChild,
        height: wrapper.firstElementChild.getBoundingClientRect().height,
        coverage: previous?.get(wrapper)?.coverage }));
    // An extra viewport inside each static wrapper lets its content stay put
    // while the next scene covers it. A negative margin preserves scroll length.
    scenes.forEach((scene, index) => {
      scene.wrapper.setAttribute('data-page-layer', '');
      scene.wrapper.style.setProperty('--rs7-overlap-size', `${scene.height}px`);
      scene.wrapper.style.setProperty('--rs7-overlap-runway', `${index < scenes.length - 1 ? this.height : 0}px`);
      scene.wrapper.style.setProperty('--rs7-overlap-top', `${Math.min(0, this.height - scene.height)}px`);
    });
    this.root.setAttribute('data-page-overlap', '');
    scenes.forEach(scene => { scene.start = scene.wrapper.getBoundingClientRect().top + window.scrollY; });
    scenes.forEach((scene, index) => { scene.end = scenes[index + 1]?.start ?? scene.start + scene.height; });
    this.overlapScenes = scenes;
    this.sceneByWrapper = new Map(scenes.map(scene => [scene.wrapper, scene]));
  }

  visibilityFor(element, y = window.scrollY) {
    if (!this.root.hasAttribute('data-page-overlap')) return 1;
    const scene = this.sceneByWrapper?.get(element.closest('#MainContent > .shopify-section'));
    if (!scene) return 1;
    // IO sees the outgoing sticky scene even after another scene covers it.
    const ratio = Math.max(0, Math.min(1, (y + this.height - scene.start) / this.height, (scene.end - y) / this.height));
    // Scroll positions round to whole pixels even when a scene has a fractional
    // height. Treat that subpixel remainder as covered for every consumer.
    return ratio <= 0.001 ? 0 : ratio >= 0.999 ? 1 : ratio;
  }

  updateCoverage(y) {
    let changed = this.coverageReset;
    this.coverageReset = false;
    (this.overlapScenes || []).forEach(scene => {
      const ratio = this.visibilityFor(scene.content, y);
      const coverage = ratio <= 0.001 ? 0 : ratio >= 0.6 ? 2 : 1;
      if (scene.coverage !== coverage) {
        scene.coverage = coverage;
        changed = true;
      }
    });
    if (changed) this.root.dispatchEvent(new CustomEvent('rs7gt:pagecoveragechange'));
  }

  clearOverlap() {
    this.root.removeAttribute('data-page-overlap');
    (this.overlapScenes || []).forEach(({ wrapper }) => {
      wrapper.removeAttribute('data-page-layer');
      ['--rs7-overlap-size', '--rs7-overlap-runway', '--rs7-overlap-top'].forEach(name => wrapper.style.removeProperty(name));
    });
    this.coverageReset ||= !!this.overlapScenes?.length;
    this.overlapScenes = [];
    this.sceneByWrapper = null;
  }

  refresh() {
    cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = 0;
      this.measureOverlap();
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const stops = [];
      this.root.querySelectorAll('#MainContent > .shopify-section').forEach(wrapper => {
        const element = wrapper.querySelector('section');
        if (!element || !wrapper.offsetHeight) return;
        const top = element === this.root.hero ? 0 : wrapper.getBoundingClientRect().top + window.scrollY;
        const collage = element.querySelector('rs7gt-collage[data-launch-collage]');
        if (collage?.launchPinned) {
          collage.setLaunchRange(top, this.height);
          for (let step = 0; step < collage.launchStepCount; step++) {
            stops.push({ element, collageStep: step,
              key: step ? `${wrapper.id}:collage:${step}` : wrapper.id,
              y: Math.max(0, Math.min(max, top + step * this.height)) });
          }
          return;
        }
        const floating = element.matches('[data-floating-scene]') ? element.closest('rs7gt-launch-floating') : null;
        if (floating?.hasAttribute('data-floating-ready')) {
          element.querySelectorAll('[data-floating-slide]').forEach((slide, step) => {
            const block = slide.dataset.floatingBlock || String(step);
            stops.push({ element, floatingStep: step,
              key: step ? `${wrapper.id}:color:${block}` : wrapper.id,
              y: Math.max(0, Math.min(max, top + step * this.height)) });
          });
          return;
        }
        if (floating) {
          // If the carousel script is unavailable, expose all static photos in
          // the section's native scroll region instead of creating empty stops.
          element.querySelectorAll('[data-floating-image]').forEach(image => {
            if (image.dataset.floatingSrcset && !image.hasAttribute('srcset')) image.srcset = image.dataset.floatingSrcset;
            if (image.dataset.floatingSrc && !image.hasAttribute('src')) image.src = image.dataset.floatingSrc;
          });
        }
        stops.push({ element, key: wrapper.id, y: Math.max(0, Math.min(max, top)) });
      });
      this.stops = stops.sort((a, b) => a.y - b.y).filter((stop, index, all) => !index || stop.y - all[index - 1].y > 2);
      this.floatingGroups = [...this.root.querySelectorAll('rs7gt-launch-floating')].map(component => ({
        component, stops: this.stops.filter(stop => stop.floatingStep !== undefined
          && stop.element === component.querySelector('[data-floating-scene]'))
      })).filter(group => group.stops.length);
      if (this.restoreKey) {
        let index = this.stops.findIndex(stop => stop.key === this.restoreKey);
        if (index < 0) {
          const key = this.restoreKey.replace(/:copy$|:color:.*$|:collage:\d+$/, '');
          // A wider viewport can reduce the collage's number of stops. Keep
          // its last available view rather than resetting the photos to start.
          if (/:collage:\d+$/.test(this.restoreKey)) {
            this.stops.forEach((stop, candidate) => {
              if (stop.key === key || stop.key.startsWith(`${key}:collage:`)) index = candidate;
            });
          } else index = this.stops.findIndex(stop => stop.key === key);
        }
        this.restoreKey = null;
        if (index >= 0) this.go(index, { instant: true });
      }
      if (!this.initialized) {
        this.initialized = true;
        this.navigateHash(true);
      }
      this.syncFloating({ instant: true });
      this.root.chapters?.refresh();
      this.root.requestScroll();
      this.queueSettle();
    });
  }

  go(index, { instant = false } = {}) {
    const stop = this.stops[Math.max(0, Math.min(this.stops.length - 1, index))];
    if (!stop) return;
    this.positionKey = stop.key;
    this.cancel();
    const start = window.scrollY;
    const distance = stop.y - start;
    const reduced = this.root.motionQuery.matches;
    if (instant || reduced || Math.abs(distance) < 2) {
      this.syncFloating({ stop, instant: true, direction: Math.sign(distance) });
      window.scrollTo(0, stop.y);
      this.root.chapters?.update();
      return;
    }
    this.targetKey = stop.key;
    this.syncFloating({ stop, direction: Math.sign(distance) });
    this.root.chapters?.update();
    const started = performance.now();
    const duration = 720;
    this.lockUntil = started + duration;
    const tick = now => {
      const progress = Math.min(1, (now - started) / duration);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      window.scrollTo(0, start + distance * eased);
      if (progress < 1) this.animation = requestAnimationFrame(tick);
      else {
        this.animation = 0;
        this.targetKey = null;
        this.root.requestScroll();
      }
    };
    this.animation = requestAnimationFrame(tick);
  }

  cancel() {
    cancelAnimationFrame(this.animation);
    clearTimeout(this.settleTimer);
    this.animation = 0;
    this.targetKey = null;
    if (this.loop) {
      this.loop.transition?.skipTransition();
      this.loop.animations?.forEach(animation => animation.cancel());
      this.loop.restoreHeader?.();
      this.loop = null;
    }
  }

  advance(direction, index = this.currentIndex()) {
    const last = this.stops[this.stops.length - 1];
    const collage = last?.element.querySelector('rs7gt-collage[data-launch-collage]');
    // Only a fresh forward gesture after the last photo view closes the loop.
    // Anchors, End, settling and responsive refreshes still use ordinary go().
    if (direction > 0 && index === this.stops.length - 1 && collage
      && this.stops[0]?.element === this.root.hero) {
      this.returnToOpening();
    } else this.go(index + direction);
  }

  returnToOpening() {
    this.cancel();
    const first = this.stops[0];
    if (!first) return;
    const loop = {};
    this.loop = loop;
    const reset = () => {
      if (this.loop !== loop || this.controller.signal.aborted) return;
      this.positionKey = first.key;
      this.syncFloating({ stop: first, instant: true });
      window.scrollTo({ top: first.y, behavior: 'instant' });
      // Update the incoming snapshot synchronously, including the fixed menu.
      window.ScrollTrigger?.update();
      this.root.animations?.forEach(animation => animation.scrollTrigger?.getTween?.()?.progress(1));
      this.updateCoverage(first.y);
      this.root.clock?.updateProgress?.(first.y, this.stops);
      this.root.updateHeroCoverage(first.y);
      this.root.showHeader();
      this.root.chapters?.update();
      this.root.chapters?.focusTarget(first.element);
      history.replaceState(history.state, '', `#${encodeURIComponent(first.element.id)}`);
      this.root.requestScroll();
    };
    const finish = () => {
      if (this.loop !== loop) return;
      // Finish at the opening even if the browser moved focus or its scrollbar
      // while the native snapshots were covering the document.
      this.positionKey = first.key;
      window.scrollTo({ top: first.y, behavior: 'instant' });
      this.root.clock?.updateProgress?.(first.y, this.stops);
      this.root.showHeader();
      this.root.chapters?.update();
      loop.restoreHeader?.();
      this.loop = null;
      this.lastWheel = performance.now();
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.wheelCandidate = null;
      this.wheelDirection = 1;
      this.lockUntil = this.lastWheel + 180;
      this.root.requestScroll();
      this.queueSettle();
    };
    if (this.root.motionQuery.matches || !document.startViewTransition || document.hidden) {
      reset();
      finish();
      return;
    }
    // Native snapshots keep the last photos still while the opening covers
    // them. The real document moves directly to zero: no cloned components,
    // intermediate chapters or backwards sweep across the entire landing.
    try {
      const header = this.root.header;
      if (header) {
        const previousName = header.style.viewTransitionName;
        header.style.viewTransitionName = 'rs7-launch-loop-navigation';
        loop.restoreHeader = () => { header.style.viewTransitionName = previousName; };
      }
      loop.transition = document.startViewTransition(reset);
      loop.transition.ready.then(() => {
        if (this.loop !== loop) return;
        const options = { duration: 720, easing: 'cubic-bezier(.65, 0, .35, 1)', fill: 'both' };
        loop.animations = [];
        loop.animations.push(document.documentElement.animate({ opacity: [1, 1], mixBlendMode: ['normal', 'normal'] },
          { ...options, pseudoElement: '::view-transition-old(root)' }));
        loop.animations.push(document.documentElement.animate({ transform: ['translateY(100%)', 'translateY(0)'],
          opacity: [1, 1], mixBlendMode: ['normal', 'normal'] },
          { ...options, pseudoElement: '::view-transition-new(root)' }));
      }).catch(() => {
        loop.transition.skipTransition();
      });
      loop.transition.finished.then(finish, finish);
    } catch (_) {
      reset();
      finish();
    }
  }

  syncFloating({ stop = this.stops[this.currentIndex()], instant = false, direction = 0 } = {}) {
    if (!stop) return;
    this.floatingGroups?.forEach(group => {
      const nearest = group.stops.reduce((selected, candidate) =>
        Math.abs(candidate.y - stop.y) < Math.abs(selected.y - stop.y) ? candidate : selected);
      group.component.setStep?.(nearest.floatingStep, { instant, direction });
    });
  }

  queueSceneSync() {
    if (this.sceneFrame) return;
    this.sceneFrame = requestAnimationFrame(() => {
      this.sceneFrame = 0;
      this.syncFloating();
    });
  }

  queueSettle() {
    clearTimeout(this.settleTimer);
    if (this.animation || this.loop || this.touch || this.scrollbarHeld || !this.stops.length) return;
    this.settleTimer = setTimeout(() => {
      if (!this.animation && !this.loop && !this.touch && !this.scrollbarHeld) this.go(this.currentIndex());
    }, 180);
  }

  isControl(target) {
    return !(target instanceof Element) || !!target.closest('input, select, textarea, button, a, summary, [contenteditable="true"], [role="slider"], [role="tablist"], dialog');
  }

  canScrollInside(target, delta, axis = 'y') {
    const horizontal = axis === 'x';
    for (let node = target; node instanceof Element && node !== this.root; node = node.parentElement) {
      const size = horizontal ? node.scrollWidth : node.scrollHeight;
      const client = horizontal ? node.clientWidth : node.clientHeight;
      const position = horizontal ? node.scrollLeft : node.scrollTop;
      if (size <= client + 2 || !/auto|scroll/.test(getComputedStyle(node)[horizontal ? 'overflowX' : 'overflowY'])) continue;
      const bounds = node.getBoundingClientRect();
      // Keyboard focus may remain in the previous chapter after paging. A
      // covered or offscreen region must not consume the next scroll gesture.
      if (bounds.bottom <= 0 || bounds.top >= this.height || bounds.right <= 0 || bounds.left >= this.width
        || this.visibilityFor(node) <= 0) continue;
      if (delta < 0 ? position > 1 : position + client < size - 1) return true;
    }
    return false;
  }

  resetWheelGesture(direction = 0) {
    this.wheelTotal = 0;
    this.wheelUsed = false;
    this.wheelDirection = direction;
    this.wheelPeak = 0;
    this.wheelSamples = [];
    this.wheelCandidate = null;
    this.wheelMomentum = false;
  }

  onWheel(event) {
    if (event.defaultPrevented || event.ctrlKey) return;
    // Read deltaMode first: wheel deltas can be pixels, lines or pages.
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.height : 1;
    const delta = event.deltaY * unit;
    const now = performance.now();
    // A View Transition retargets pointer input to HTML, outside the launch
    // root. Consume the gesture before checking its target or inner scrollers.
    if (this.loop) {
      this.lastWheel = now;
      this.wheelTotal = 0;
      this.wheelUsed = true;
      event.preventDefault();
      return;
    }
    if (this.root.chapters?.containsOpenPanel(event.target)) {
      if (!this.canScrollInside(event.target, delta)) event.preventDefault();
      return;
    }
    if (!this.root.contains(event.target)
      || event.target.closest('select, textarea, input, [contenteditable="true"], dialog')
      || Math.abs(event.deltaX) > Math.abs(event.deltaY) || !event.deltaY) return;
    const gap = now - this.lastWheel;
    const direction = Math.sign(delta);
    const blocked = !!this.animation || now < this.lockUntil;
    const fresh = gap > 180 || (this.wheelMomentum && event.momentum === false);
    if (fresh) {
      // Slow, small packets may arrive farther apart than the gesture gap.
      // Accumulate them until a page has actually been requested.
      const pending = !this.wheelUsed && gap < 400 && direction === this.wheelDirection ? this.wheelTotal : 0;
      this.resetWheelGesture(direction);
      this.wheelTotal = pending;
      this.wheelUsed = blocked;
    }
    this.lastWheel = now;
    if (this.canScrollInside(event.target, delta)) {
      this.wheelUsed = true;
      return;
    }
    event.preventDefault();
    // Use the platform's inertia flag when exposed. The fallback below also
    // works in browsers which don't report the physical gesture's phases.
    if (event.momentum === true) {
      this.wheelMomentum = true;
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.wheelCandidate = null;
      return;
    }
    this.wheelMomentum = false;
    const magnitude = Math.abs(delta);
    if (this.wheelUsed && !fresh && !blocked) {
      const candidate = this.wheelCandidate;
      if (candidate && candidate.direction === direction && magnitude >= candidate.minimum) {
        candidate.total += delta;
        candidate.count++;
      } else {
        const floor = this.wheelSamples.length === 3 ? Math.max(...this.wheelSamples) : Infinity;
        const reversed = direction !== this.wheelDirection;
        const minimum = reversed ? 4 : Math.max(4, floor * 1.8);
        this.wheelCandidate = (reversed || floor <= this.wheelPeak * 0.35) && magnitude >= minimum
          ? { direction, minimum, total: delta, count: 1 } : null;
      }
      // A new stroke can start before the previous inertia stops. Require
      // three sustained packets so an isolated momentum spike cannot page.
      if (this.wheelCandidate?.count >= 3 && Math.abs(this.wheelCandidate.total) >= 24) {
        this.resetWheelGesture(direction);
        this.wheelUsed = true;
        this.wheelPeak = magnitude;
        this.wheelSamples.push(magnitude);
        this.advance(direction);
        return;
      }
    }
    this.wheelPeak = Math.max(this.wheelPeak, magnitude);
    this.wheelSamples.push(magnitude);
    if (this.wheelSamples.length > 3) this.wheelSamples.shift();
    if (blocked) {
      // Never enqueue a gesture which began during a page transition.
      this.wheelUsed = true;
      this.wheelTotal = 0;
      this.wheelCandidate = null;
    }
    if (this.wheelUsed) return;
    if (direction !== this.wheelDirection) {
      this.wheelTotal = 0;
      this.wheelDirection = direction;
    }
    this.wheelTotal += delta;
    if (Math.abs(this.wheelTotal) < 24) return;
    this.wheelUsed = true;
    this.advance(Math.sign(this.wheelTotal));
  }

  onTouchStart(event) {
    this.touch = null;
    if (event.touches.length !== 1 || (!this.loop && !this.root.contains(event.target))
      || event.target.closest('input, select, textarea, dialog')) return;
    const point = event.touches[0];
    this.touch = {
      x: point.clientX, y: point.clientY, target: event.target, axis: null,
      originKey: this.stops[this.currentIndex()]?.key,
      used: !!this.loop || !!this.animation || performance.now() < this.lockUntil
    };
  }

  touchThreshold() {
    return 44;
  }

  onTouchMove(event) {
    const touch = this.touch;
    if (this.loop && event.touches.length === 1) {
      if (event.cancelable) event.preventDefault();
      if (touch) touch.used = true;
      return;
    }
    if (!touch || event.touches.length !== 1 || event.defaultPrevented) return;
    const dx = touch.x - event.touches[0].clientX;
    const dy = touch.y - event.touches[0].clientY;
    if (this.root.chapters?.containsOpenPanel(touch.target)) {
      if (!this.canScrollInside(touch.target, dy) && event.cancelable) event.preventDefault();
      return;
    }
    if (touch.used) {
      if (event.cancelable) event.preventDefault();
      return;
    }
    if (!touch.axis) {
      if (Math.abs(dx) > Math.abs(dy) * 1.2) {
        if (this.canScrollInside(touch.target, 1, 'x') || this.canScrollInside(touch.target, -1, 'x')) touch.axis = 'native-horizontal';
        else if (touch.target.closest('rs7gt-product-rotator')) touch.axis = 'horizontal';
      } else if (Math.abs(dy) > Math.abs(dx) * 1.2) {
        touch.axis = this.canScrollInside(touch.target, dy) ? 'internal' : 'vertical';
      }
    }
    if (touch.axis === 'internal' || touch.axis === 'native-horizontal') return;
    if (event.cancelable) event.preventDefault();
    if (touch.axis !== 'vertical') return;
    touch.target.closest('rs7gt-product-rotator')?.finishDrag?.();
    if (Math.abs(dy) < this.touchThreshold(touch) || this.animation || this.loop || performance.now() < this.lockUntil) return;
    touch.used = true;
    const index = this.stops.findIndex(stop => stop.key === touch.originKey);
    if (index >= 0) this.advance(Math.sign(dy), index);
  }

  onKey(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
    const directions = { ArrowDown: 1, PageDown: 1, ArrowUp: -1, PageUp: -1, ' ': event.shiftKey ? -1 : 1 };
    const direction = directions[event.key];
    if (!direction && event.key !== 'Home' && event.key !== 'End') return;
    if (this.loop) {
      event.preventDefault();
      return;
    }
    if (this.isControl(event.target)) return;
    if (this.canScrollInside(event.target, direction || (event.key === 'Home' ? -1 : 1))) return;
    const chapter = this.stops[this.currentIndex()]?.element;
    if (direction && chapter) {
      const region = [chapter, ...chapter.querySelectorAll('*')].find(node => {
        const bounds = node.getBoundingClientRect();
        return bounds.bottom > 0 && bounds.top < this.height && bounds.right > 0 && bounds.left < this.width
          && this.visibilityFor(node) > 0
          && node.scrollHeight > node.clientHeight + 2 && /auto|scroll/.test(getComputedStyle(node).overflowY)
          && (direction < 0 ? node.scrollTop > 1 : node.scrollTop + node.clientHeight < node.scrollHeight - 1);
      });
      if (region) {
        event.preventDefault();
        region.scrollBy(0, direction * (event.key.startsWith('Arrow') ? 40 : region.clientHeight * 0.8));
        return;
      }
    }
    event.preventDefault();
    if (event.repeat || this.animation || this.loop || performance.now() < this.lockUntil) return;
    if (direction) this.advance(direction);
    else this.go(event.key === 'Home' ? 0 : this.stops.length - 1);
  }

  stopFor(element) {
    return this.stops.findIndex(stop => stop.element === element || stop.element.contains(element));
  }

  navigateHash(instant = false) {
    let target;
    try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch (_) { return; }
    if (!target) return;
    const index = target.id === 'MainContent' ? 0 : this.stopFor(target);
    if (index >= 0) this.go(index, { instant });
  }

  onLink(event) {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey
      || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
    let target;
    try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (_) { return; }
    if (!target) return;
    const index = target.id === 'MainContent' ? 0 : this.stopFor(target);
    if (index < 0) return;
    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
    this.go(index);
    if (link.closest('[data-launch-chapters]') && this.root.chapters) this.root.chapters.focusTarget(target);
    else if (target.matches('[tabindex]')) target.focus({ preventScroll: true });
  }

  onFocus(event) {
    if (this.animation || this.loop) return;
    const index = this.stopFor(event.target);
    if (index < 0) return;
    const current = this.stops[this.currentIndex()];
    if (current?.element !== this.stops[index].element) this.go(index, { instant: true });
  }

  destroy() {
    this.controller.abort();
    window.ScrollTrigger?.removeEventListener('refresh', this.onTriggerRefresh);
    window.ScrollTrigger?.removeEventListener('matchMedia', this.onMediaRefresh);
    this.cancel();
    cancelAnimationFrame(this.measureFrame);
    cancelAnimationFrame(this.sceneFrame);
    this.clearOverlap();
    this.root.removeAttribute('data-page-scroll');
    this.root.style.removeProperty('--rs7-page-height');
    document.documentElement.removeAttribute('data-rs7-page-scroll');
  }
};
